/**
 * Композабл для управления кастомизацией продукта (addons/excludes).
 * Single Responsibility — только логика кастомизации.
 */

import { ref, computed, watch, type Ref } from 'vue';
import type { ProductVariant } from './types/product.types';

export interface UseProductCustomizationOptions {
  currentVariant: Ref<ProductVariant>;
  showLimit?: number;
}

export function useProductCustomization(options: UseProductCustomizationOptions) {
  const { currentVariant, showLimit = 8 } = options;

  const selectedAddons = ref<number[]>([]);
  const selectedExcludes = ref<number[]>([]);
  const showAllAddons = ref(false);
  const showAllExcludes = ref(false);

  const visibleAddons = computed(() => {
    if (!currentVariant.value?.addons) return [];
    return showAllAddons.value
      ? currentVariant.value.addons
      : currentVariant.value.addons.slice(0, showLimit);
  });

  const visibleExcludes = computed(() => {
    if (!currentVariant.value?.excludes) return [];
    return showAllExcludes.value
      ? currentVariant.value.excludes
      : currentVariant.value.excludes.slice(0, showLimit);
  });

  const hasMoreAddons = computed(
    () => (currentVariant.value?.addons?.length || 0) > showLimit && !showAllAddons.value,
  );
  const hasMoreExcludes = computed(
    () => (currentVariant.value?.excludes?.length || 0) > showLimit && !showAllExcludes.value,
  );

  const addonsSum = computed<number>(() =>
    selectedAddons.value.reduce((sum, addonId) => {
      const addon = currentVariant.value.addons?.find(a => a.id === addonId);
      return sum + (addon?.price ?? 0);
    }, 0),
  );

  const totalPrice = computed<number>(
    () => (currentVariant.value?.price ?? 0) + addonsSum.value,
  );

  const hasCustomization = computed(() => {
    const hasAddons = (currentVariant.value.addons?.length ?? 0) > 0;
    const hasExcludes = (currentVariant.value.excludes?.length ?? 0) > 0;
    return hasAddons || hasExcludes;
  });

  const toggleAddon = (addonId: number) => {
    const index = selectedAddons.value.indexOf(addonId);
    if (index > -1) selectedAddons.value.splice(index, 1);
    else selectedAddons.value.push(addonId);
  };

  const toggleExclude = (excludeId: number) => {
    const index = selectedExcludes.value.indexOf(excludeId);
    if (index > -1) selectedExcludes.value.splice(index, 1);
    else selectedExcludes.value.push(excludeId);
  };

  const resetCustomization = () => {
    selectedAddons.value = [];
    selectedExcludes.value = [];
    showAllAddons.value = false;
    showAllExcludes.value = false;
  };

  watch(currentVariant, () => { resetCustomization(); });

  const getSortedIds = (ids: number[]) => ids.map(String).sort();

  return {
    selectedAddons,
    selectedExcludes,
    showAllAddons,
    showAllExcludes,
    visibleAddons,
    visibleExcludes,
    hasMoreAddons,
    hasMoreExcludes,
    addonsSum,
    totalPrice,
    hasCustomization,
    toggleAddon,
    toggleExclude,
    resetCustomization,
    getSortedIds,
  };
}
